import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { API_URL } from './authService';

function Accommodations() {
  const [accommodations, setAccommodations] = useState([]);
  const [destinations, setDestinations] = useState([]); // For the dropdown
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [accommodationToDelete, setAccommodationToDelete] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingAccommodation, setViewingAccommodation] = useState(null);
  const [searchField, setSearchField] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const pageSize = 10;
  const [selectedFile1, setSelectedFile1] = useState(null);
  const [selectedFile2, setSelectedFile2] = useState(null);
  const [modalImageUrl1, setModalImageUrl1] = useState('');
  const [modalImageUrl2, setModalImageUrl2] = useState('');
  const [isModalImageLoading1, setIsModalImageLoading1] = useState(false);
  const [isModalImageLoading2, setIsModalImageLoading2] = useState(false);
  // Estados para el buscador de destinos en el formulario
  const [destinationSearchTerm, setDestinationSearchTerm] = useState('');
  const [showDestinationSuggestions, setShowDestinationSuggestions] = useState(false);
  const [filteredDestinationsList, setFilteredDestinationsList] = useState([]);
  const { token } = useAuth();

  const fetchAccommodations = useCallback(async (page = 0, sort = '', search = '') => {
    if (!token) return;
    setLoading(true);
    try {
      const sortParam = sort ? `&sort=${sort}` : '';
      let searchParam = search ? `&${search}` : '';

      // Complex search for destination name
      if (search && search.includes('destination.destinationName.contains')) {
        const value = search.split('=')[1];
        const destResponse = await fetch(`${API_URL}/api/destinations?destinationName.contains=${value}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (destResponse.ok) {
          const dests = await destResponse.json();
          const destIds = dests.map(d => d.id);
          if (destIds.length > 0) {
            searchParam = `&destinationId.in=${destIds.join(',')}`;
          } else {
            setAccommodations([]); setTotalElements(0); setTotalPages(0); setLoading(false); return;
          }
        }
      }

      // Complex search for destination country
      if (search && search.includes('destination.country.contains')) {
        const value = search.split('=')[1];
        const destResponse = await fetch(`${API_URL}/api/destinations?country.contains=${value}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (destResponse.ok) {
          const dests = await destResponse.json();
          const destIds = dests.map(d => d.id);
          if (destIds.length > 0) {
            searchParam = `&destinationId.in=${destIds.join(',')}`;
          } else {
            setAccommodations([]); setTotalElements(0); setTotalPages(0); setLoading(false); return;
          }
        }
      }

      const response = await fetch(`${API_URL}/api/acommodations?page=${page}&size=${pageSize}${sortParam}${searchParam}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      let data = await response.json();

      // Fetch full destination details for each accommodation
      data = await Promise.all(
        data.map(async (acc) => {
          let destinationData = null;
          if (acc.destination && acc.destination.id) {
            try {
              const destResponse = await fetch(`${API_URL}/api/destinations/${acc.destination.id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              if (destResponse.ok) destinationData = await destResponse.json();
            } catch (error) { console.error('Error fetching destination data:', error); }
          }
          return { ...acc, destination: destinationData };
        })
      );
      
      const totalCount = parseInt(response.headers.get('X-Total-Count') || '0');
      const totalPages = Math.ceil(totalCount / pageSize);
      
      setAccommodations(data);
      setTotalPages(totalPages);
      setTotalElements(totalCount);
      setCurrentPage(page);
    } catch (error) {
      console.error('Error fetching accommodations:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch destinations for the form dropdown
  useEffect(() => {
    const fetchAllDestinations = async () => {
      if (!token) return;
      try {
        const response = await fetch(`${API_URL}/api/destinations?size=2000`, { // Fetch a large number
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setDestinations(data);
        }
      } catch (error) {
        console.error('Error fetching destinations for form:', error);
      }
    };
    fetchAllDestinations();
  }, [token]);

  useEffect(() => {
    fetchAccommodations(0, sortField ? `${sortField},${sortDirection}` : '');
  }, [fetchAccommodations, sortField, sortDirection]);

  const handlePageChange = (newPage) => {
    const sortParam = sortField ? `${sortField},${sortDirection}` : '';
    const searchParam = buildSearchParam();
    fetchAccommodations(newPage, sortParam, searchParam);
  };

  const handleSort = (field) => {
    const newDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(newDirection);
  };

  const getSortArrow = (field) => {
    if (sortField !== field) return ' ↕';
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  const buildSearchParam = () => {
    if (!searchField || !searchValue.trim()) return '';
    
    const fieldMap = {
      'acommodationName': 'acommodationName.contains',
      'destinationName': 'destination.destinationName.contains',
      'country': 'destination.country.contains'
    };
    
    const apiField = fieldMap[searchField];
    return apiField ? `${apiField}=${encodeURIComponent(searchValue.trim())}` : '';
  };

  const handleSearchFieldChange = (e) => {
    setSearchField(e.target.value);
    setSearchValue('');
    setCurrentPage(0);
    if (!e.target.value) {
      const sortParam = sortField ? `${sortField},${sortDirection}` : '';
      fetchAccommodations(0, sortParam);
    }
  };

  const handleSearchValueChange = (e) => {
    setSearchValue(e.target.value);
    setCurrentPage(0);
  };

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      if (searchField && searchValue.trim()) {
        const sortParam = sortField ? `${sortField},${sortDirection}` : '';
        const searchParam = buildSearchParam();
        fetchAccommodations(0, sortParam, searchParam);
      } else if (searchField && !searchValue.trim()) {
        const sortParam = sortField ? `${sortField},${sortDirection}` : '';
        fetchAccommodations(0, sortParam);
      }
    }, 300);
    return () => clearTimeout(debounceTimeout);
  }, [searchValue, searchField, sortField, sortDirection, fetchAccommodations]);

  useEffect(() => {
    let objectUrl1, objectUrl2;

    const fetchModalImage = async (position) => {
        if (!viewingAccommodation || !viewingAccommodation.id || !token) {
            if (position === 1) setModalImageUrl1('');
            if (position === 2) setModalImageUrl2('');
            return;
        }

        if (position === 1) setIsModalImageLoading1(true);
        else setIsModalImageLoading2(true);

        try {
            const response = await fetch(`${API_URL}/api/acommodations/${viewingAccommodation.id}/image/${position}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok && response.headers.get('Content-Type')?.startsWith('image')) {
                const imageBlob = await response.blob();
                const url = URL.createObjectURL(imageBlob);
                if (position === 1) {
                    objectUrl1 = url;
                    setModalImageUrl1(url);
                } else {
                    objectUrl2 = url;
                    setModalImageUrl2(url);
                }
            } else {
                if (position === 1) setModalImageUrl1('');
                else setModalImageUrl2('');
            }
        } catch (error) {
            console.error(`Error fetching modal image ${position}:`, error);
            if (position === 1) setModalImageUrl1('');
            else setModalImageUrl2('');
        } finally {
            if (position === 1) setIsModalImageLoading1(false);
            else setIsModalImageLoading2(false);
        }
    };

    if (viewingAccommodation) {
      fetchModalImage(1);
      fetchModalImage(2);
    }

    return () => {
        if (objectUrl1) URL.revokeObjectURL(objectUrl1);
        if (objectUrl2) URL.revokeObjectURL(objectUrl2);
    };
  }, [viewingAccommodation, token]);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    acommodationName: '',
    acommodationDescription: '',
    destination: { id: '' },
    acommodationImageUrlOne: '',
    acommodationImageUrlTwo: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'destination') {
      setFormData({ ...formData, destination: { id: value ? parseInt(value) : null } });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleDestinationSearchChange = (e) => {
    const value = e.target.value;
    setDestinationSearchTerm(value);
    setFormData(prev => ({ ...prev, destination: { id: '' } }));
    
    if (value.trim()) {
      const filtered = destinations
        .filter(d => 
          d.destinationName.toLowerCase().includes(value.toLowerCase()) || 
          d.country.toLowerCase().includes(value.toLowerCase())
        )
        .sort((a, b) => a.destinationName.localeCompare(b.destinationName))
        .slice(0, 10);
      setFilteredDestinationsList(filtered);
    } else {
      setFilteredDestinationsList(destinations.sort((a, b) => a.destinationName.localeCompare(b.destinationName)).slice(0, 10));
    }
    setShowDestinationSuggestions(true);
  };

  const selectDestinationFromSearch = (dest) => {
    setDestinationSearchTerm(`${dest.destinationName} - ${dest.country}`);
    setFormData(prev => ({ ...prev, destination: { id: dest.id } }));
    setShowDestinationSuggestions(false);
  };

  const handleFileChange = (e, position) => {
    const file = e.target.files[0];
    if (file && (file.type === 'image/jpeg' || file.type === 'image/jpg')) {
        if (position === 1) setSelectedFile1(file);
        else setSelectedFile2(file);
    } else {
        alert('Por favor, selecciona solo archivos .jpg o .jpeg');
        e.target.value = null; // Clear the input
        if (position === 1) setSelectedFile1(null);
        else setSelectedFile2(null);
    }
  };

  const createAccommodation = async (accommodationData) => {
    const response = await fetch(`${API_URL}/api/acommodations`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(accommodationData)
    });
    if (!response.ok) throw new Error('Failed to create accommodation');
    return response.json();
  };

  const updateAccommodation = async (id, accommodationData) => {
    const response = await fetch(`${API_URL}/api/acommodations/${id}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ ...accommodationData, id })
    });
    if (!response.ok) throw new Error('Failed to update accommodation');
    return response.json();
  };

  const deleteAccommodation = async (id) => {
    const response = await fetch(`${API_URL}/api/acommodations/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) throw new Error('Failed to delete accommodation');
  };

  const uploadImage = async (accommodationId, file, position) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('position', position);

    try {
        const response = await fetch(`${API_URL}/api/acommodations/${accommodationId}/upload-image`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
            },
            body: formData
        });

        if (!response.ok) {
            throw new Error(`Error al subir la imagen ${position}.`);
        }
        console.log(`Imagen ${position} subida exitosamente.`);
    } catch (error) {
        console.error(`Error en la subida de imagen ${position}:`, error);
        alert(`El hospedaje se guardó, pero hubo un error al subir la imagen ${position}.`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.destination.id) {
      alert('Por favor, seleccione un destino.');
      return;
    }
    try {
      let savedAcc;

      if (editingId) {
        // --- ACTUALIZAR ---
        const payload = { ...formData };
        // Si se sube una nueva imagen, actualizamos la URL esperada en la BD
        if (selectedFile1) payload.acommodationImageUrlOne = `${API_URL}/api/acommodations/${editingId}/image/1`;
        if (selectedFile2) payload.acommodationImageUrlTwo = `${API_URL}/api/acommodations/${editingId}/image/2`;
        
        savedAcc = await updateAccommodation(editingId, payload);

        if (selectedFile1) await uploadImage(editingId, selectedFile1, "1");
        if (selectedFile2) await uploadImage(editingId, selectedFile2, "2");

      } else {
        // --- CREAR ---
        // 1. Crear entidad sin URLs primero para obtener ID
        const { acommodationImageUrlOne, acommodationImageUrlTwo, ...initialPayload } = formData;
        savedAcc = await createAccommodation(initialPayload);
        
        const id = savedAcc.id;
        let needsUpdate = false;
        let finalPayload = { ...initialPayload, id };

        // 2. Subir imágenes y preparar payload final con URLs
        if (selectedFile1) {
            await uploadImage(id, selectedFile1, "1");
            finalPayload.acommodationImageUrlOne = `${API_URL}/api/acommodations/${id}/image/1`;
            needsUpdate = true;
        }
        if (selectedFile2) {
            await uploadImage(id, selectedFile2, "2");
            finalPayload.acommodationImageUrlTwo = `${API_URL}/api/acommodations/${id}/image/2`;
            needsUpdate = true;
        }

        // 3. Actualizar con URLs si hubo subidas
        if (needsUpdate) {
            await updateAccommodation(id, finalPayload);
        }
      }
      resetForm();
      const sortParam = sortField ? `${sortField},${sortDirection}` : '';
      const searchParam = buildSearchParam();
      fetchAccommodations(currentPage, sortParam, searchParam);
    } catch (error) {
      console.error('Error saving accommodation:', error);
      alert('Error al guardar el hospedaje.');
    }
  };

  const handleEdit = (accommodation) => {
    setFormData({
      id: accommodation.id,
      acommodationName: accommodation.acommodationName,
      acommodationDescription: accommodation.acommodationDescription,
      destination: { id: accommodation.destination?.id || '' },
      acommodationImageUrlOne: accommodation.acommodationImageUrlOne || '',
      acommodationImageUrlTwo: accommodation.acommodationImageUrlTwo || ''
    });
    if (accommodation.destination) {
      setDestinationSearchTerm(`${accommodation.destination.destinationName} - ${accommodation.destination.country}`);
    } else {
      setDestinationSearchTerm('');
    }
    setEditingId(accommodation.id);
    setSelectedFile1(null);
    setSelectedFile2(null);
    setShowForm(true);
  };

  const handleView = (accommodation) => {
    setViewingAccommodation(accommodation);
    setShowViewModal(true);
  };

  const handleDelete = (id) => {
    setAccommodationToDelete(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (accommodationToDelete) {
      try {
        await deleteAccommodation(accommodationToDelete);
        const sortParam = sortField ? `${sortField},${sortDirection}` : '';
        const searchParam = buildSearchParam();
        fetchAccommodations(currentPage, sortParam, searchParam);
      } catch (error) {
        console.error('Error deleting accommodation:', error);
        alert('Error al eliminar el hospedaje. Puede que esté asociado a un presupuesto.');
      }
      setShowDeleteModal(false);
      setAccommodationToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setAccommodationToDelete(null);
  };

  const resetForm = () => {
    setFormData({
      acommodationName: '',
      acommodationDescription: '',
      destination: { id: '' },
      acommodationImageUrlOne: '',
      acommodationImageUrlTwo: ''
    });
    setDestinationSearchTerm('');
    setShowForm(false);
    setEditingId(null);
    setSelectedFile1(null);
    setSelectedFile2(null);
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>Gestión de Hospedajes</h2>
        <button onClick={() => { resetForm(); setShowForm(true); }} style={styles.addButton}>
          + Agregar Hospedaje
        </button>
      </div>

      <div style={styles.searchContainer}>
        <div style={styles.searchField}>
          <label style={styles.searchLabel}>Buscar por:</label>
          <select 
            value={searchField} 
            onChange={handleSearchFieldChange} 
            style={styles.searchSelect}
          >
            <option value="">Seleccionar campo</option>
            <option value="acommodationName">Nombre del Hospedaje</option>
            <option value="destinationName">Nombre del Destino</option>
            <option value="country">País del Destino</option>
          </select>
        </div>
        {searchField && (
          <div style={styles.searchField}>
            <label style={styles.searchLabel}>Valor:</label>
            <input
              type="text"
              value={searchValue}
              onChange={handleSearchValueChange}
              placeholder={`Buscar por ${searchField === 'acommodationName' ? 'nombre' : searchField === 'destinationName' ? 'destino' : 'país'}...`}
              style={styles.searchInput}
            />
          </div>
        )}
        {(searchField || searchValue) && (
          <button 
            onClick={() => {
              setSearchField('');
              setSearchValue('');
              setCurrentPage(0);
              const sortParam = sortField ? `${sortField},${sortDirection}` : '';
              fetchAccommodations(0, sortParam);
            }}
            style={styles.clearButton}
          >
            Limpiar
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} style={styles.form}>
          <h3 style={styles.formTitle}>{editingId ? 'Editar Hospedaje' : 'Nuevo Hospedaje'}</h3>
          <div style={styles.formGrid}>
            <div style={styles.fieldContainer}>
              <label style={styles.label}>Nombre del Hospedaje</label>
              <input name="acommodationName" value={formData.acommodationName} onChange={handleInputChange} style={styles.input} required />
            </div>
            <div style={styles.fieldContainer}>
              <label style={styles.label}>Destino</label>
              <div style={styles.autocompleteContainer}>
                <input 
                  type="text" 
                  placeholder="Buscar destino..." 
                  value={destinationSearchTerm} 
                  onChange={handleDestinationSearchChange}
                  onFocus={() => {
                    if (!destinationSearchTerm) setFilteredDestinationsList(destinations.sort((a, b) => a.destinationName.localeCompare(b.destinationName)).slice(0, 10));
                    setShowDestinationSuggestions(true);
                  }}
                  onBlur={() => setTimeout(() => setShowDestinationSuggestions(false), 200)}
                  style={styles.input} 
                  required
                  autoComplete="off"
                />
                {showDestinationSuggestions && (
                  <div style={styles.suggestionsDropdown}>
                    {filteredDestinationsList.map(dest => (
                      <div key={dest.id} style={styles.suggestionItem} onClick={() => selectDestinationFromSearch(dest)}>
                        {dest.destinationName} - {dest.country}
                      </div>
                    ))}
                    {filteredDestinationsList.length === 0 && (
                      <div style={styles.suggestionItem}>No se encontraron destinos</div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div style={styles.fieldContainer}>
              <label style={styles.label}>Imagen 1 (JPG)</label>
              <input type="file" accept=".jpg, .jpeg" onChange={(e) => handleFileChange(e, 1)} style={styles.input} />
            </div>
            <div style={styles.fieldContainer}>
              <label style={styles.label}>Imagen 2 (JPG)</label>
              <input type="file" accept=".jpg, .jpeg" onChange={(e) => handleFileChange(e, 2)} style={styles.input} />
            </div>
            <div style={{...styles.fieldContainer, gridColumn: '1 / -1', marginTop: '10px'}}>
              <label style={styles.label}>Descripción</label>
              <textarea name="acommodationDescription" value={formData.acommodationDescription} onChange={handleInputChange} style={styles.textarea} />
            </div>
          </div>
          <div style={styles.formButtons}>
            <button type="button" onClick={resetForm} style={styles.cancelButton}>Cancelar</button>
            <button type="submit" style={styles.saveButton}>Guardar</button>
          </div>
        </form>
      )}

      <div style={styles.tableContainer}>
        {loading ? (
          <div style={styles.emptyState}>Cargando hospedajes...</div>
        ) : accommodations.length === 0 ? (
          <div style={styles.emptyState}>
            No hay hospedajes registrados.
          </div>
        ) : (
          <>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.sortableHeader} onClick={() => handleSort('acommodationName')}>Nombre{getSortArrow('acommodationName')}</th>
                  <th style={styles.tableHeader}>Descripción</th>
                  <th style={styles.sortableHeader} onClick={() => handleSort('destination.destinationName')}>Destino - País{getSortArrow('destination.destinationName')}</th>
                  <th style={styles.tableHeader}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {accommodations.map(acc => (
                  <tr key={acc.id}>
                    <td style={styles.tableCell}>{acc.acommodationName}</td>
                    <td style={{...styles.tableCell, maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} title={acc.acommodationDescription}>{acc.acommodationDescription}</td>
                    <td style={styles.tableCell}>{acc.destination ? `${acc.destination.destinationName} - ${acc.destination.country}` : 'N/A'}</td>
                    <td style={styles.tableCell}>
                      <div style={styles.actionButtons}>
                        <button onClick={() => handleView(acc)} style={styles.viewButton}>Ver</button>
                        <button onClick={() => handleEdit(acc)} style={styles.editButton}>Editar</button>
                        <button onClick={() => handleDelete(acc.id)} style={styles.deleteButton}>Eliminar</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={styles.pagination}>
              <div style={styles.paginationInfo}>
                Mostrando {currentPage * pageSize + 1} - {Math.min((currentPage + 1) * pageSize, totalElements)} de {totalElements} hospedajes
              </div>
              <div style={styles.paginationControls}>
                <button 
                  onClick={() => handlePageChange(currentPage - 1)} 
                  disabled={currentPage === 0}
                  style={currentPage === 0 ? styles.paginationButtonDisabled : styles.paginationButton}
                >
                  Anterior
                </button>
                <span style={styles.pageInfo}>Página {currentPage + 1} de {totalPages}</span>
                <button 
                  onClick={() => handlePageChange(currentPage + 1)} 
                  disabled={currentPage >= totalPages - 1}
                  style={currentPage >= totalPages - 1 ? styles.paginationButtonDisabled : styles.paginationButton}
                >
                  Siguiente
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {showViewModal && viewingAccommodation && (
        <div style={styles.modalOverlay}>
          <div style={styles.viewModal}>
            <h3 style={styles.modalTitle}>Información del Hospedaje</h3>
            <div style={styles.viewContent}>
              <div style={styles.viewRow}><strong>Nombre:</strong> {viewingAccommodation.acommodationName}</div>
              <div style={styles.viewRow}><strong>Destino:</strong> {viewingAccommodation.destination ? `${viewingAccommodation.destination.destinationName} - ${viewingAccommodation.destination.country}` : 'N/A'}</div>
              <div style={styles.viewRow}><strong>Descripción:</strong> {viewingAccommodation.acommodationDescription}</div>
              
              <div style={styles.viewRow}>
                <strong>Imagen 1:</strong>
                <div style={{ marginTop: '10px' }}>
                  {isModalImageLoading1 ? (
                    <p>Cargando...</p>
                  ) : modalImageUrl1 ? (
                    <img src={modalImageUrl1} alt="Alojamiento 1" style={{ maxWidth: '100%', height: 'auto', border: '2px solid #ddd', borderRadius: '8px' }} />
                  ) : (
                    <p style={{fontStyle: 'italic', color: '#888'}}>No disponible</p>
                  )}
                </div>
              </div>

              <div style={styles.viewRow}>
                <strong>Imagen 2:</strong>
                <div style={{ marginTop: '10px' }}>
                  {isModalImageLoading2 ? (
                    <p>Cargando...</p>
                  ) : modalImageUrl2 ? (
                    <img src={modalImageUrl2} alt="Alojamiento 2" style={{ maxWidth: '100%', height: 'auto', border: '2px solid #ddd', borderRadius: '8px' }} />
                  ) : (
                    <p style={{fontStyle: 'italic', color: '#888'}}>No disponible</p>
                  )}
                </div>
              </div>
            </div>
            <div style={styles.modalButtons}>
              <button onClick={() => setShowViewModal(false)} style={styles.modalCancelButton}>Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>Confirmar Eliminación</h3>
            <p style={styles.modalText}>¿Estás seguro de que deseas eliminar este hospedaje?</p>
            <div style={styles.modalButtons}>
              <button onClick={cancelDelete} style={styles.modalCancelButton}>Cancelar</button>
              <button onClick={confirmDelete} style={styles.modalConfirmButton}>Eliminar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Re-using styles from Destinations.js as requested
const styles = {
  container: { padding: '5px', maxWidth: '100%', margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' },
  title: { fontSize: '36px', fontWeight: 'bold', color: '#333', margin: 0 },
  addButton: { backgroundColor: '#ff9f43', color: 'white', padding: '16px 32px', border: 'none', borderRadius: '8px', fontSize: '20px', fontWeight: '500', cursor: 'pointer', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' },
  form: { backgroundColor: 'white', padding: '30px', borderRadius: '12px', marginBottom: '30px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', border: '1px solid #e9ecef' },
  formTitle: { fontSize: '28px', fontWeight: '600', marginBottom: '20px', color: '#333' },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px', marginBottom: '25px' },
  fieldContainer: { display: 'flex', flexDirection: 'column' },
  label: { fontSize: '16px', fontWeight: 'bold', color: '#495057', marginBottom: '4px' },
  input: { padding: '16px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '18px', backgroundColor: '#fff' },
  textarea: { padding: '16px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '18px', backgroundColor: '#fff', minHeight: '80px', resize: 'vertical' },
  select: { padding: '16px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '18px', backgroundColor: '#fff' },
  formButtons: { display: 'flex', gap: '12px', justifyContent: 'flex-end' },
  saveButton: { backgroundColor: '#28a745', color: 'white', padding: '16px 32px', border: 'none', borderRadius: '6px', fontSize: '18px', fontWeight: '500', cursor: 'pointer' },
  cancelButton: { backgroundColor: '#6c757d', color: 'white', padding: '16px 32px', border: 'none', borderRadius: '6px', fontSize: '18px', fontWeight: '500', cursor: 'pointer' },
  tableContainer: { backgroundColor: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', border: '1px solid #e9ecef' },
  table: { width: '100%', borderCollapse: 'collapse' },
  tableHeader: { backgroundColor: '#f8f9fa', fontWeight: '600', padding: '20px 40px', textAlign: 'left', borderBottom: '2px solid #dee2e6', color: '#495057', width: '180px', minWidth: '180px', fontSize: '18px' },
  sortableHeader: { backgroundColor: '#f8f9fa', fontWeight: '600', padding: '20px 40px', textAlign: 'left', borderBottom: '2px solid #dee2e6', color: '#495057', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', fontSize: '18px' },
  tableCell: { padding: '20px 40px', borderBottom: '1px solid #dee2e6', fontSize: '18px' },
  actionButtons: { display: 'flex', gap: '8px', whiteSpace: 'nowrap' },
  editButton: { backgroundColor: '#17a2b8', color: 'white', padding: '10px 20px', border: 'none', borderRadius: '4px', fontSize: '16px', cursor: 'pointer' },
  deleteButton: { backgroundColor: '#dc3545', color: 'white', padding: '10px 20px', border: 'none', borderRadius: '4px', fontSize: '16px', cursor: 'pointer' },
  viewButton: { backgroundColor: '#28a745', color: 'white', padding: '10px 20px', border: 'none', borderRadius: '4px', fontSize: '16px', cursor: 'pointer' },
  emptyState: { textAlign: 'center', padding: '40px', color: '#6c757d', fontSize: '20px' },
  pagination: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px', borderTop: '1px solid #dee2e6', backgroundColor: '#f8f9fa' },
  paginationInfo: { fontSize: '18px', color: '#6c757d' },
  paginationControls: { display: 'flex', alignItems: 'center', gap: '15px' },
  paginationButton: { padding: '12px 24px', backgroundColor: '#ff9f43', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '18px' },
  paginationButtonDisabled: { padding: '12px 24px', backgroundColor: '#e9ecef', color: '#6c757d', border: 'none', borderRadius: '4px', cursor: 'not-allowed', fontSize: '18px' },
  pageInfo: { fontSize: '18px', fontWeight: '500', color: '#495057' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
  modal: { backgroundColor: 'white', padding: '30px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.3)', maxWidth: '400px', width: '90%' },
  modalTitle: { fontSize: '28px', fontWeight: '600', marginBottom: '15px', color: '#333', textAlign: 'center' },
  modalText: { fontSize: '20px', color: '#495057', marginBottom: '25px', textAlign: 'center', lineHeight: '1.5' },
  modalButtons: { display: 'flex', gap: '12px', justifyContent: 'center' },
  modalCancelButton: { backgroundColor: '#6c757d', color: 'white', padding: '16px 32px', border: 'none', borderRadius: '6px', fontSize: '18px', fontWeight: '500', cursor: 'pointer' },
  modalConfirmButton: { backgroundColor: '#dc3545', color: 'white', padding: '16px 32px', border: 'none', borderRadius: '6px', fontSize: '18px', fontWeight: '500', cursor: 'pointer' },
  viewModal: { backgroundColor: 'white', padding: '30px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.3)', maxWidth: '600px', width: '90%', maxHeight: '85vh', overflowY: 'auto' },
  viewContent: { marginBottom: '25px' },
  viewRow: { padding: '12px 0', borderBottom: '1px solid #e9ecef', fontSize: '20px' },
  searchContainer: { backgroundColor: 'white', padding: '20px', borderRadius: '12px', marginBottom: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', display: 'flex', gap: '15px', alignItems: 'end', flexWrap: 'wrap' },
  searchField: { display: 'flex', flexDirection: 'column', minWidth: '200px' },
  searchLabel: { fontSize: '14px', fontWeight: '600', color: '#495057', marginBottom: '5px' },
  searchSelect: { padding: '12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '16px', backgroundColor: '#fff' },
  searchInput: { padding: '12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '16px', backgroundColor: '#fff', minWidth: '250px' },
  clearButton: { backgroundColor: '#6c757d', color: 'white', padding: '12px 20px', border: 'none', borderRadius: '6px', fontSize: '16px', cursor: 'pointer', height: 'fit-content' },
  autocompleteContainer: { position: 'relative' },
  suggestionsDropdown: { position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: 'white', border: '1px solid #ddd', borderTop: 'none', borderRadius: '0 0 6px 6px', maxHeight: '200px', overflowY: 'auto', zIndex: 1000, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
  suggestionItem: { padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid #f1f1f1', fontSize: '16px' }
};

export default Accommodations;